import React, { useMemo } from 'react'
import { AdvancedDropdown } from '../AdvancedDropdown'
import {
  DropdownOption,
  DropdownSection,
} from '../AdvancedDropdown/AdvancedDropdown.types'
import {
  Language,
  LanguageSelectorProps,
  LanguageGroup,
} from './LanguageSelector.types'
import {
  LanguageSelectorWrapper,
  LanguageOption,
  LanguageFlag,
  LanguageInfo,
  LanguageName,
  LanguageNativeName,
  LanguageCode,
  CurrentLanguage,
  PopularBadge,
} from './LanguageSelector.styles'

// Default languages with flags
const defaultLanguages: Language[] = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' },
]

// Language groups for organized display
const languageGroups: LanguageGroup[] = [
  {
    id: 'popular',
    name: 'Popular Languages',
    languages: ['en', 'fr', 'de', 'es', 'zh'].map(
      (code) => defaultLanguages.find((lang) => lang.code === code)!
    ),
  },
  {
    id: 'europe',
    name: 'European',
    languages: ['it', 'pt', 'nl', 'pl', 'sv', 'no', 'da', 'fi'].map(
      (code) => defaultLanguages.find((lang) => lang.code === code)!
    ),
  },
  {
    id: 'asia',
    name: 'Asian',
    languages: ['ja', 'ko', 'hi', 'ar', 'tr'].map(
      (code) => defaultLanguages.find((lang) => lang.code === code)!
    ),
  },
  {
    id: 'other',
    name: 'Other',
    languages: ['ru'].map(
      (code) => defaultLanguages.find((lang) => lang.code === code)!
    ),
  },
]

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  value,
  onChange,
  languages = defaultLanguages,
  placeholder = 'Select language',
  disabled = false,
  size = 'medium',
  variant = 'default',
  position = 'bottom',
  width,
  className,
  showNativeNames = true,
  groupByRegion = false,
  emptyMessage = 'No languages available',
}) => {
  const selectedLanguage = useMemo(
    () => languages.find((lang) => lang.code === value),
    [languages, value]
  )

  const dropdownOptions = useMemo(() => {
    if (groupByRegion) {
      // Create sections based on language groups
      const sections: DropdownSection[] = languageGroups
        .map((group) => {
          const groupLanguages = group.languages.filter((lang) =>
            languages.some((l) => l.code === lang.code)
          )

          if (groupLanguages.length === 0) return null

          return {
            id: group.id,
            title: group.name,
            options: groupLanguages.map((lang) => ({
              id: lang.code,
              label: lang.name,
              description: showNativeNames ? lang.nativeName : undefined,
              icon: (
                <LanguageFlag className="language-flag">
                  {lang.flag}
                </LanguageFlag>
              ),
              data: lang,
            })),
          }
        })
        .filter(Boolean) as DropdownSection[]

      return { sections, options: undefined }
    } else {
      // Flat list of options
      const options: DropdownOption[] = languages.map((lang) => ({
        id: lang.code,
        label: lang.name,
        description: showNativeNames ? lang.nativeName : undefined,
        icon: (
          <LanguageFlag className="language-flag">{lang.flag}</LanguageFlag>
        ),
        data: lang,
      }))

      return { sections: undefined, options }
    }
  }, [languages, groupByRegion, showNativeNames])

  const handleChange = (
    newValue: string | string[],
    option?: DropdownOption | DropdownOption[]
  ) => {
    if (typeof newValue === 'string' && option && !Array.isArray(option)) {
      onChange(newValue, option.data as Language)
    }
  }

  const renderOption = (option: DropdownOption) => (
    <LanguageOption>
      {option.icon}
      {variant !== 'flag-only' && (
        <LanguageInfo>
          <LanguageName className="language-name">{option.label}</LanguageName>
          {showNativeNames && option.description && (
            <LanguageNativeName>{option.description}</LanguageNativeName>
          )}
        </LanguageInfo>
      )}
      {variant === 'default' && (
        <LanguageCode className="language-code">{option.id}</LanguageCode>
      )}
      {option.id === 'en' && groupByRegion && (
        <PopularBadge>Popular</PopularBadge>
      )}
    </LanguageOption>
  )

  const renderValue = () => {
    if (!selectedLanguage) return null

    return (
      <CurrentLanguage className="language-display">
        <LanguageFlag className="language-flag">
          {selectedLanguage.flag}
        </LanguageFlag>
        {variant !== 'flag-only' && (
          <span className="language-name">{selectedLanguage.name}</span>
        )}
        {variant === 'default' && (
          <LanguageCode className="language-code">
            {selectedLanguage.code}
          </LanguageCode>
        )}
      </CurrentLanguage>
    )
  }

  return (
    <LanguageSelectorWrapper
      className={className}
      $variant={variant}
      $size={size}
    >
      <AdvancedDropdown
        value={value}
        onChange={handleChange}
        options={dropdownOptions.options}
        sections={dropdownOptions.sections}
        placeholder={placeholder}
        disabled={disabled}
        size={size}
        position={position}
        width={width || (variant === 'flag-only' ? 'auto' : undefined)}
        searchable={languages.length > 10}
        searchPlaceholder="Search languages..."
        clearable={false}
        renderOption={renderOption}
        renderValue={renderValue}
        emptyMessage={emptyMessage}
        noResultsMessage="No languages found"
      />
    </LanguageSelectorWrapper>
  )
}
